import type { CharterDisplayMessage } from '../components/Shared/CharterChatMessages'
import type {
  GroupChatMessage,
  PrepChatMessage,
} from '../redux/types/charterWsTypes'

/**
 * Immer leaves unchanged messages at the same reference, so caching on it stops
 * every bubble re-rendering (and re-parsing markdown) on each stream chunk.
 */
const prepCache = new WeakMap<
  PrepChatMessage,
  { key: string; out: CharterDisplayMessage }
>()
const groupCache = new WeakMap<
  GroupChatMessage,
  { key: string; out: CharterDisplayMessage }
>()

/** Identity alone would rely on an Immer guarantee that is not documented, so
 *  the key also carries what changes during a stream. */
const cacheKey = (
  m: { content?: string; is_streaming?: boolean },
  scope: string
) => `${scope}|${m.is_streaming ? 1 : 0}|${m.content?.length ?? 0}`

/** Map Phase-1 prep WS messages to the shared chat display format. */
export function toPrepDisplayMessages(
  messages: PrepChatMessage[],
  userName: string
): CharterDisplayMessage[] {
  return messages.map((m) => {
    const key = cacheKey(m, userName)
    const hit = prepCache.get(m)
    if (hit && hit.key === key) return hit.out
    const out: CharterDisplayMessage = {
      id: m.id,
      content: m.content,
      isUser: m.role === 'user',
      isAgent: m.role === 'assistant',
      senderName: m.role === 'user' ? userName : 'Preparation Coach',
      sectionKey: m.section_key ?? null,
      isStreaming: m.is_streaming,
      createdAt: m.created_at,
    }
    prepCache.set(m, { key, out })
    return out
  })
}

/** Map Phase-2 group WS messages to the shared chat display format. */
export function toGroupDisplayMessages(
  messages: GroupChatMessage[],
  currentUserId: string
): CharterDisplayMessage[] {
  return messages.map((m) => {
    const key = cacheKey(m, currentUserId)
    const hit = groupCache.get(m)
    if (hit && hit.key === key) return hit.out
    const out: CharterDisplayMessage = {
      id: m.id,
      content: m.content,
      isUser: m.role === 'user' && String(m.sender_id) === currentUserId,
      isAgent: m.role === 'moderator',
      senderId: m.sender_id != null ? String(m.sender_id) : null,
      senderName:
        m.role === 'moderator' ? 'Group Moderator' : m.sender_display_name,
      isStreaming: m.is_streaming,
      isIntervention: m.is_intervention,
      createdAt: m.created_at,
    }
    groupCache.set(m, { key, out })
    return out
  })
}

/** Default section ordering used when the WS section_order isn't available yet. */
export const DEFAULT_SECTION_ORDER = [
  'skills',
  'goals',
  'obstacles',
  'commitments',
  'role_preferences',
  'communication_prefs',
  'tool_familiarity',
  'meeting_prefs',
  'boundaries',
  'leadership_preference',
  'conflict_decision_prefs',
]
